/**
 * Public-website affiliate signed-URL client (task 6.2, public-website spec).
 *
 * Implements the two-hop signed-URL transport defined by parent
 * `dvgup-platform` design §4.5 and visualized in this sub-spec's
 * design §8.2 (Game-page on-demand affiliate hydration). The SPA
 * uses this client when a `GetGameByIdV1` response carries an empty
 * `affiliateLinks` array — the `<GamePage>` component (task 6.9)
 * dispatches an affiliate thunk that calls
 * {@link getAffiliateLinks} which:
 *
 *  1. **Mints** a short-lived signed URL by POSTing
 *     `{ gameTitle }` to the public REST gateway's mint endpoint
 *     (`/MintAffiliateLookupUrlV1` per design §8.2). The mint hop is
 *     gated by the public REST API key (`x-api-key`); the response
 *     carries `{ signedUrl, expiresAt }`.
 *  2. **Fetches** the signed URL with a plain `GET` — no
 *     `x-api-key`, no `Authorization` — because the signed URL
 *     itself encodes the GET-target authorization (IAM SigV4 query
 *     params per parent §4.5 / `affiliate-integration` design §4.4).
 *     The response carries `{ links: [...] }` per the
 *     `AffiliateLinkLookup` v1 shape.
 *
 * Empty `links: []` is a normal outcome per R12.6 and the
 * `affiliate-integration` Manager contract — the SPA renders the
 * Game page without affiliate buttons rather than throwing.
 *
 * Why no SWR cache (`./swr-cache.ts`)?
 * - The current SWR cache key is `URL + Authorization + x-api-key`
 *   (see `./swr-cache.ts#buildCacheKey`); the request body is **not**
 *   part of the key. Two consecutive `getAffiliateLinks({ gameTitle:
 *   "A" })` and `getAffiliateLinks({ gameTitle: "B" })` calls would
 *   therefore collide on the mint hop's cache slot, returning B's
 *   request the signed URL minted for A. The signed URL itself is
 *   single-use / time-bound, so caching it provides no benefit on the
 *   GET hop either. This client therefore goes straight through
 *   `httpClient` + raw `fetch` — the affiliate thunk runs once per
 *   Game-page mount, so cache wins are negligible regardless.
 *
 * TODO(smithy-follow-up-A): The mint operation
 * `MintAffiliateLookupUrlV1`, its request envelope
 * `{ gameTitle: string }`, and its response envelope
 * `{ signedUrl: string, expiresAt: string }` are not yet in the
 * Smithy IDL. Per the tasks.md "Implementation gating" preamble,
 * the same `smithy-api-layer` follow-up A that adds
 * `ApprovedContentApi.{ListDaysWithSummariesV1,GetContentV1}` and
 * `ProfileApi` v1 will also publish this operation under the public
 * REST gateway and regenerate the TS types under
 * `@dvgup/contracts/generated/`. The shapes below mirror the design
 * §8.2 sequence diagram verbatim and will be replaced by a single
 * `import type { ... }` swap once follow-up A lands. The downstream
 * `{ links: [...] }` shape is owned by `affiliate-integration` (its
 * Manager's `AffiliateLookupOutput`) and will be sourced from
 * `@dvgup/contracts/affiliate-link-lookup` post-migration.
 *
 * Function-length budget: every public export below stays under 20
 * lines per Requirement 3.7. The two hops are decomposed into
 * `mintSignedUrl` and `fetchSignedUrl` so `getAffiliateLinks` itself
 * is a three-line composition.
 */

import { httpClient } from './http-client';

/**
 * Affiliate-link reference returned by the signed-URL GET hop.
 * Mirrors the `affiliate-integration` Manager's `links[]` member
 * (`{ asin, url, title, image }`) projected to the two fields the
 * SPA actually renders in `<AffiliateLinks>` (task 5.4).
 *
 * TODO(smithy-follow-up-A): replace with the regenerated import
 * from `@dvgup/contracts/affiliate-link-lookup`.
 */
export interface AffiliateLink {
  /** Vendor identifier (e.g., `"amazon"`) — drives the button icon. */
  readonly vendor: string;
  /** Absolute URL with the affiliate `tag={affiliateId}` query param embedded. */
  readonly url: string;
}

/**
 * Input shape for {@link getAffiliateLinks}. The `gameTitle` is the
 * canonical title from the `GameDetail` record (task 6.1's
 * `getGameById` response); the affiliate Lambda normalizes it before
 * the PA-API search per `affiliate-integration` design §5.
 *
 * TODO(smithy-follow-up-A): replace with the regenerated import
 * from `@dvgup/contracts/generated/affiliate-link-lookup`.
 */
export interface GetAffiliateLinksInput {
  readonly gameTitle: string;
}

/**
 * Output shape for {@link getAffiliateLinks}. Empty `links: []` is
 * the normal "no matches" outcome per R12.6 — the caller renders
 * the Game page without affiliate buttons rather than treating it
 * as an error.
 *
 * TODO(smithy-follow-up-A): replace with the regenerated import
 * from `@dvgup/contracts/affiliate-link-lookup`.
 */
export interface GetAffiliateLinksOutput {
  readonly links: ReadonlyArray<AffiliateLink>;
}

/**
 * Mint-endpoint response envelope per design §8.2. The SPA hands
 * the `signedUrl` straight to {@link fetchSignedUrl}; `expiresAt`
 * is captured for diagnostic logging only — the SPA does not retry
 * on expiration (the affiliate thunk runs once per Game-page mount
 * and the URL TTL comfortably exceeds a single page load).
 *
 * TODO(smithy-follow-up-A): replace with the regenerated import
 * from `@dvgup/contracts/generated/affiliate-link-lookup`. The
 * canonical IDL field name (`signedUrl` vs `url`) will be locked
 * in by the follow-up; the design §8.2 sequence diagram shows
 * `url` shorthand, this surface uses `signedUrl` for clarity.
 */
export interface MintAffiliateLookupUrlOutput {
  readonly signedUrl: string;
  readonly expiresAt: string;
}

/**
 * Per-call options for the affiliate signed-URL client. Mirrors the
 * `GamesApiClientOptions` shape from `./games-api-client.ts` so the
 * affiliate thunk (task 6.5) threads the same `(baseUrl, apiKey)`
 * tuple through every public-gateway call.
 */
export interface AffiliateSignedUrlClientOptions {
  /** API origin — `"/api"` in production, dev-server proxy origin locally. */
  readonly baseUrl: string;
  /** Public REST API key from `/config.json`; injected as `x-api-key` on hop 1 only. */
  readonly apiKey?: string;
}

/**
 * Hop 1 of the signed-URL transport: POST to the public gateway's
 * mint endpoint with the public REST API key. Returns the
 * short-lived signed URL the SPA fetches in hop 2.
 *
 * @param input The game-title input.
 * @param options Base URL + public REST API key from `/config.json`.
 * @returns The mint envelope (`signedUrl` + `expiresAt`).
 * @throws Error when the mint hop returns a non-2xx response.
 */
async function mintSignedUrl(
  input: GetAffiliateLinksInput,
  options: AffiliateSignedUrlClientOptions,
): Promise<MintAffiliateLookupUrlOutput> {
  const response = await httpClient(`${options.baseUrl}/MintAffiliateLookupUrlV1`, {
    apiKey: options.apiKey,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new Error(`MintAffiliateLookupUrlV1 failed with status ${response.status}`);
  }
  return (await response.json()) as MintAffiliateLookupUrlOutput;
}

/**
 * Hop 2 of the signed-URL transport: plain `GET` of the signed URL
 * with **no** `x-api-key` and **no** `Authorization` header — the
 * signed URL embeds its own auth (IAM SigV4 query parameters per
 * parent §4.5 / `affiliate-integration` design §4.4). Adding either
 * header would mismatch the signature and break the request.
 *
 * @param signedUrl The short-lived URL returned by hop 1.
 * @returns The affiliate-links payload.
 * @throws Error when the signed-URL GET returns a non-2xx response.
 */
async function fetchSignedUrl(signedUrl: string): Promise<GetAffiliateLinksOutput> {
  const response = await fetch(signedUrl);
  if (!response.ok) {
    throw new Error(`Affiliate signed-URL GET failed with status ${response.status}`);
  }
  return (await response.json()) as GetAffiliateLinksOutput;
}

/**
 * Fetch the affiliate links for a game via the two-hop signed-URL
 * transport per parent §4.5 / design §8.2 / R8.6.
 *
 * Empty `links: []` is the normal "no PA-API matches" outcome per
 * R12.6 — the caller renders the Game page without affiliate
 * buttons rather than treating it as an error.
 *
 * @param input The game-title input.
 * @param options Base URL + public REST API key from `/config.json`.
 * @returns The affiliate-links payload (possibly empty).
 * @throws Error when either hop returns a non-2xx response.
 */
export async function getAffiliateLinks(
  input: GetAffiliateLinksInput,
  options: AffiliateSignedUrlClientOptions,
): Promise<GetAffiliateLinksOutput> {
  const mint = await mintSignedUrl(input, options);
  return fetchSignedUrl(mint.signedUrl);
}
